import { env } from '../config/env'
import { isNativeApp } from './platform'

/**
 * Links to this app itself (e.g. `https://ascendmessenger.com.au/?setup=recording-consent`
 * in a chat message) must navigate IN the app, not open an external browser —
 * on native that would bounce the person out to the web sign-in. The router
 * installs a navigation handler here (see `usePushNotifications`); `linkify-text`
 * asks before falling back to `openExternalUrl`.
 */

type AppLinkNavigationHandler = (path: string) => void
let navigationHandler: AppLinkNavigationHandler | null = null

export function setAppLinkNavigationHandler(fn: AppLinkNavigationHandler | null): void {
  navigationHandler = fn
}

/** The in-app path for a URL that points at this app, or null for anything else. */
export function appPathForUrl(href: string): string | null {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  // `https://app.test//evil.com/x` has our origin but a protocol-relative
  // pathname: handed to the router, pushState would resolve it CROSS-origin
  // (and react-router's fallback would location.assign there). Never ours.
  if (url.pathname.startsWith('//')) return null
  const origins = new Set<string>()
  try {
    origins.add(new URL(env.publicAppUrl).origin)
  } catch {
    /* unset/malformed public URL: only the running origin matches */
  }
  // On native the WebView origin is capacitor://localhost — never a valid target.
  if (!isNativeApp() && typeof window !== 'undefined') origins.add(window.location.origin)
  if (!origins.has(url.origin)) return null
  return `${url.pathname}${url.search}${url.hash}` || '/'
}

/** Navigate in-app when the URL is ours and a handler is installed. */
export function openAppLink(href: string): boolean {
  const path = appPathForUrl(href)
  if (!path || !navigationHandler) return false
  navigationHandler(path)
  return true
}
